/** @format */
import angular from 'angular';
import _ from 'lodash';
import { localeServiceModule, localizedStringFilterModule } from 'sis-common';
import { creditRangeFilterModule } from '../number/creditRange.filter';
import { searchParameterModelsModule } from './searchParameterModels';

export const searchParameterServiceModule = 'sis-components.search.searchParameterService';

(function () {
    searchParametersFactory.$inject = [
        'AndParameter',
        'OrParameter',
        'HiddenParameter',
        'Parameter',
        'JsDataObjectParameter',
        'JsHierarchicalDataObjectParameter',
        'UrnObjectParameter',
        'DateRangeParameter',
        'SalesPeriodParameter',
        'RangeParameter',
        'StringParameter',
        'PersonParameter',
        'ValidityPeriodParameter',
        'DateParameter',
        'BooleanParameter',
        'JsDataObjectWithGroupIdParameter',
        'StudyTermLocatorParameter',
        'localeService',
        'SEARCH_FLOWS_STATE',
        'searchDefaults',
    ];
    angular
        .module(searchParameterServiceModule, [
            creditRangeFilterModule,
            localeServiceModule,
            localizedStringFilterModule,
            searchParameterModelsModule,
        ])
        .factory('SearchParameters', searchParametersFactory)
        .constant('searchDefaults', {
            start: 0,
            limit: 50,
            maxLimit: 999,
        })
        .constant('SEARCH_FLOWS_STATE', {
            NOT_READY: 'NOT_READY',
            PUBLISHED_ACTIVE: 'PUBLISHED_ACTIVE',
            PUBLISHED_FUTURE: 'PUBLISHED_FUTURE',
            CANCELLED: 'CANCELLED',
            ARCHIVED: 'ARCHIVED',
            values() {
                return _.filter(_.mapValues(this), value => !_.isFunction(value));
            },
        });

    function searchParametersFactory( // NOSONAR
        AndParameter,
        OrParameter,
        HiddenParameter,
        Parameter,
        JsDataObjectParameter,
        JsHierarchicalDataObjectParameter,
        UrnObjectParameter,
        DateRangeParameter,
        SalesPeriodParameter,
        RangeParameter,
        StringParameter,
        PersonParameter,
        ValidityPeriodParameter,
        DateParameter,
        BooleanParameter,
        JsDataObjectWithGroupIdParameter,
        StudyTermLocatorParameter,
        localeService,
        SEARCH_FLOWS_STATE,
        searchDefaults,
    ) {
        function SearchParameters(overridingDefaults, options) {
            /**
             * Parameters that will be added to query parameters after other parameters have been resolved. These (overridingDefaults)
             * parameters override query parameters for equal keys.
             *
             * You probably should not define same parameter as overriding default and as a user editable parameter. Backend search will be
             * performed with overriding default parameter ignoring any actions done by user. UI will probably look like search is performed
             * with user defined parameters which is not true.
             *
             * These (overridingDefaults) are not a way to implement pre-selected parameters. For example you might want to define that it
             * should initialise with documentState=ACTIVE,DRAFT query parameters but you also want to enable other document states for
             * user.
             */
            this.overridingDefaults = overridingDefaults;

            this.searchString = new StringParameter(AndParameter, options);

            // These are all combined to codeUrn when making request!
            this.officialLanguageUrns = new UrnObjectParameter(OrParameter);
            this.studyLevelUrns = new UrnObjectParameter(OrParameter);
            this.studyFieldUrns = new UrnObjectParameter(OrParameter);
            this.courseUnitTypeUrns = new UrnObjectParameter(OrParameter);
            this.cefrLevelUrns = new UrnObjectParameter(OrParameter);
            this.subjectUrns = new UrnObjectParameter(OrParameter);
            this.degreeProgrammeTypeUrns = new UrnObjectParameter(OrParameter);
            this.educationClassificationModelUrns = new UrnObjectParameter(OrParameter);
            this.municipalityUrns = new UrnObjectParameter(OrParameter);
            this.courseUnitRealisationTypeUrn = new UrnObjectParameter(OrParameter);
            this.attainmentLanguageUrns = new UrnObjectParameter(OrParameter);
            this.attainmentDate = new DateRangeParameter(OrParameter);
            this.studyRightSelectionType = new UrnObjectParameter(OrParameter);

            // These cur-prefixed URNs are all combined to curCodeUrn when making request!
            this.curTeachingLanguageUrns = new UrnObjectParameter(OrParameter);

            this.educationTypeUrns = new UrnObjectParameter(OrParameter);
            this.assessmentItemType = new UrnObjectParameter(OrParameter);
            this.assessmentItemId = new JsDataObjectParameter(OrParameter);

            this.curriculumPeriods = new JsDataObjectParameter(OrParameter);
            this.responsibilityInfos = new PersonParameter(AndParameter);
            this.flowStates = new Parameter(OrParameter);
            this.productStates = new Parameter(OrParameter);

            this.studyPeriods = new ValidityPeriodParameter(OrParameter);
            this.studyYears = new ValidityPeriodParameter(OrParameter);
            this.studyTerms = new ValidityPeriodParameter(OrParameter);
            this.attendingTermRegistrations = new StudyTermLocatorParameter(OrParameter);
            this.nonAttendingTermRegistrations = new StudyTermLocatorParameter(OrParameter);
            this.studyRightSelectedOptions = new JsDataObjectWithGroupIdParameter(OrParameter);
            this.studyRightSelectedChildOptions = new JsDataObjectWithGroupIdParameter(OrParameter);
            this.studyRightMinorSelections = new JsDataObjectWithGroupIdParameter(OrParameter);
            this.studyRightModuleSelections = new JsDataObjectWithGroupIdParameter(OrParameter);
            this.courseUnits = new JsDataObjectWithGroupIdParameter(OrParameter);
            this.studyModules = new JsDataObjectWithGroupIdParameter(OrParameter);

            this.organisations = new JsDataObjectParameter(OrParameter);
            this.organisationRoots = new JsDataObjectParameter(OrParameter);
            this.studyRightOrganisations = new JsDataObjectParameter(OrParameter);
            this.studyRightParentOrganisations = new JsDataObjectParameter(OrParameter);

            this.educations = new JsHierarchicalDataObjectParameter(OrParameter);
            this.activePhases = new JsDataObjectWithGroupIdParameter(OrParameter);

            this.approvalStates = new UrnObjectParameter(OrParameter);
            this.documentStates = new Parameter(OrParameter);

            this.dateRange = new DateRangeParameter(OrParameter);
            this.studyRightTuitionFeeObligationPeriodRanges = new DateRangeParameter(OrParameter);
            this.dateOfBirth = new DateParameter(OrParameter);
            this.salesPeriod = new SalesPeriodParameter(OrParameter);
            this.validityPeriod = new SalesPeriodParameter(OrParameter);
            this.salesPeriodValidity = new Parameter(OrParameter);

            this.creditRange = new RangeParameter(AndParameter);

            this.motherTongue = new UrnObjectParameter(OrParameter);
            this.preferredLanguage = new UrnObjectParameter(OrParameter);
            this.schoolEducationLanguage = new UrnObjectParameter(OrParameter);
            this.citizenship = new UrnObjectParameter(OrParameter);
            this.gender = new UrnObjectParameter(OrParameter);
            this.municipality = new UrnObjectParameter(OrParameter);
            this.educationType = new UrnObjectParameter(OrParameter);
            this.degreeProgramType = new UrnObjectParameter(OrParameter);
            this.studyRightExpirationRulesUrn = new UrnObjectParameter(OrParameter);
            this.studyRightState = new Parameter(OrParameter);
            this.dead = new Parameter(OrParameter);
            this.personGroupType = new Parameter(OrParameter);
            this.personGroupUpdateType = new Parameter(OrParameter);
            this.applicationType = new Parameter(OrParameter);
            this.applicationState = new Parameter(OrParameter);
            this.workflowType = new Parameter(OrParameter);
            this.workflowState = new Parameter(OrParameter);
            this.responsiblePersonId = new Parameter(OrParameter);
            this.responsibleTutorId = new Parameter(OrParameter);
            this.responsibleInHandlingId = new Parameter(OrParameter);
            this.responsibleType = new Parameter(AndParameter);

            this.attainmentType = new Parameter(OrParameter);
            this.attainmentState = new Parameter(OrParameter);

            this.attainmentPrimary = new Parameter(AndParameter);
            this.attainmentMisregistration = new Parameter(AndParameter);
            this.attainmentAttached = new Parameter(AndParameter);

            this.qualificationType = new Parameter(OrParameter);
            this.qualificationState = new Parameter(OrParameter);
            this.competencyState = new Parameter(OrParameter);

            this.moduleType = new Parameter(OrParameter);
            this.messageType = new Parameter(OrParameter);
            this.enrolmentState = new Parameter(OrParameter);
            this.studySubGroupId = new JsDataObjectParameter(OrParameter);
            this.messageType = new Parameter(OrParameter);
            this.messageReadState = new Parameter(OrParameter);

            // These parameters are displayed and selected using custom selection components
            this.universityOrgId = new Parameter(HiddenParameter, 'name');
            this.customCurriculumPeriods = new JsDataObjectParameter(HiddenParameter, 'name');

            this.cooperationNetworkUniversityOrgId = new JsDataObjectParameter(OrParameter);
            this.cooperationNetworks = new JsDataObjectParameter(OrParameter);
            this.cooperationNetworkUniversities = new JsDataObjectParameter(OrParameter);
            this.validityInCooperationNetwork = new Parameter(OrParameter);
            this.excludeUniversityOrgId = new JsDataObjectParameter(OrParameter);

            this.sort = new StringParameter(HiddenParameter);
            this.showMaxResults = new BooleanParameter(HiddenParameter);

            this.start = undefined;
            this.limit = undefined;
            this.uiLang = undefined;
            this.validity = undefined;

            // An optional parameter that can be used to (conditionally) skip the default HTTP error response
            // handling in RequestErrorInterceptor. Can be either true (in which case the default error response
            // handling will always be skipped), or a function that returns a boolean value. The function receives
            // the error response as its only argument. If the function returns true, the default error handling
            // will be skipped. Can be used to e.g. prevent showing a system error modal when a user isn't
            // authorized to do something (and instead indicate that in a more user-friendly way).
            this.bypassErrorInterceptor = undefined;
        }

        SearchParameters.prototype.isValid = function () {
            return (
                this.searchString.isValidForSearch() ||
                this.officialLanguageUrns.isValidForSearch() ||
                this.attainmentLanguageUrns.isValidForSearch() ||
                this.attainmentDate.isValidForSearch() ||
                this.assessmentItemType.isValidForSearch() ||
                this.assessmentItemId.isValidForSearch() ||
                this.educationTypeUrns.isValidForSearch() ||
                this.studyLevelUrns.isValidForSearch() ||
                this.studyFieldUrns.isValidForSearch() ||
                this.courseUnitTypeUrns.isValidForSearch() ||
                this.cefrLevelUrns.isValidForSearch() ||
                this.subjectUrns.isValidForSearch() ||
                this.degreeProgrammeTypeUrns.isValidForSearch() ||
                this.educationClassificationModelUrns.isValidForSearch() ||
                this.municipalityUrns.isValidForSearch() ||
                this.courseUnitRealisationTypeUrn.isValidForSearch() ||
                this.dateOfBirth.isValidForSearch() ||
                this.salesPeriod.isValidForSearch() ||
                this.validityPeriod.isValidForSearch() ||
                this.salesPeriodValidity.isValidForSearch() ||
                this.organisations.isValidForSearch() ||
                this.studyRightOrganisations.isValidForSearch() ||
                this.studyRightParentOrganisations.isValidForSearch() ||
                this.organisationRoots.isValidForSearch() ||
                this.educations.isValidForSearch() ||
                this.approvalStates.isValidForSearch() ||
                this.flowStates.isValidForSearch() ||
                this.studyYears.isValidForSearch() ||
                this.studyTerms.isValidForSearch() ||
                this.studyRightSelectedOptions.isValidForSearch() ||
                this.studyRightSelectedChildOptions.isValidForSearch() ||
                this.studyRightMinorSelections.isValidForSearch() ||
                this.studyRightModuleSelections.isValidForSearch() ||
                this.courseUnits.isValidForSearch() ||
                this.studyModules.isValidForSearch() ||
                this.attendingTermRegistrations.isValidForSearch() ||
                this.nonAttendingTermRegistrations.isValidForSearch() ||
                this.studyPeriods.isValidForSearch() ||
                this.curriculumPeriods.isValidForSearch() ||
                this.responsibilityInfos.isValidForSearch() ||
                this.motherTongue.isValidForSearch() ||
                this.preferredLanguage.isValidForSearch() ||
                this.schoolEducationLanguage.isValidForSearch() ||
                this.citizenship.isValidForSearch() ||
                this.gender.isValidForSearch() ||
                this.municipality.isValidForSearch() ||
                this.educationType.isValidForSearch() ||
                this.degreeProgramType.isValidForSearch() ||
                this.studyRightExpirationRulesUrn.isValidForSearch() ||
                this.studyRightState.isValidForSearch() ||
                this.dead.isValidForSearch() ||
                this.personGroupType.isValidForSearch() ||
                this.personGroupUpdateType.isValidForSearch() ||
                this.applicationState.isValidForSearch() ||
                this.applicationType.isValidForSearch() ||
                this.workflowState.isValidForSearch() ||
                this.workflowType.isValidForSearch() ||
                this.responsiblePersonId.isValidForSearch() ||
                this.responsibleTutorId.isValidForSearch() ||
                this.responsibleInHandlingId.isValidForSearch() ||
                this.responsibleType.isValidForSearch() ||
                this.attainmentType.isValidForSearch() ||
                this.attainmentState.isValidForSearch() ||
                this.attainmentPrimary.isValidForSearch() ||
                this.attainmentMisregistration.isValidForSearch() ||
                this.attainmentAttached.isValidForSearch() ||
                this.qualificationType.isValidForSearch() ||
                this.qualificationState.isValidForSearch() ||
                this.competencyState.isValidForSearch() ||
                this.studyRightSelectionType.isValidForSearch() ||
                this.curTeachingLanguageUrns.isValidForSearch() ||
                this.moduleType.isValidForSearch() ||
                this.messageType.isValidForSearch() ||
                this.enrolmentState.isValidForSearch() ||
                this.studySubGroupId.isValidForSearch() ||
                this.messageType.isValidForSearch() ||
                this.messageReadState.isValidForSearch() ||
                this.studyRightTuitionFeeObligationPeriodRanges.isValidForSearch() ||
                this.activePhases.isValidForSearch() ||
                this.cooperationNetworks.isValidForSearch() ||
                this.cooperationNetworkUniversities.isValidForSearch() ||
                this.validityInCooperationNetwork.isValidForSearch() ||
                this.excludeUniversityOrgId.isValidForSearch() ||
                this.cooperationNetworkUniversityOrgId.isValidForSearch() ||
                (this.searchString.displayValue() === '' && this.searchString.allowEmptySearchString)
            );
        };

        SearchParameters.prototype.setOverridingDefaults = function (overridingDefaults) {
            this.overridingDefaults = overridingDefaults;
        };

        /**
         * Assign default values for keys contained in argument. Other defaults will be kept.
         */
        SearchParameters.prototype.mergeOverridingDefaults = function (overridingDefaults) {
            if (_.isEmpty(this.overridingDefaults)) {
                this.overridingDefaults = {};
            }
            _.assign(this.overridingDefaults, overridingDefaults);
        };

        SearchParameters.prototype.fetchLimitedResults = function () {
            this.start = searchDefaults.start;
            this.limit = searchDefaults.limit;
            this.showMaxResults.toggleValue(false);
            return this;
        };

        SearchParameters.prototype.fetchAllResults = function () {
            this.start = searchDefaults.start;
            this.limit = searchDefaults.maxLimit;
            this.showMaxResults.toggleValue(true);
            return this;
        };

        SearchParameters.prototype.clearAll = function () {
            _.forEach(_.keys(this), key => {
                if (!_.isNil(this[key]) && this[key].removeAllValues) {
                    this[key].removeAllValues();
                }
            });
        };

        SearchParameters.prototype.build = function () {
            const requestOptions = {
                params: {
                    uiLang: this.uiLang ? this.uiLang : localeService.getCurrentLocale(),
                    start: this.start ? this.start : searchDefaults.start,
                    limit: this.limit ? this.limit : searchDefaults.limit,
                    ignoreValidityPeriod: true,
                },
                bypassErrorInterceptor: this.bypassErrorInterceptor,
            };
            const searchParams = requestOptions.params;

            function appendFlowState(flowState) {
                if (!searchParams.flowState) {
                    searchParams.flowState = [];
                }
                searchParams.flowState.push(flowState);
            }

            function getValueIfValid(parameter) {
                if (parameter.isValidForSearch()) {
                    return parameter.getValueInSearchFormat();
                }
                return undefined;
            }

            searchParams.fullTextQuery = getValueIfValid(this.searchString);
            searchParams.curriculumPeriodId = getValueIfValid(this.curriculumPeriods) || getValueIfValid(this.customCurriculumPeriods);
            searchParams.orgId = getValueIfValid(this.organisations);
            searchParams.orgRootId = getValueIfValid(this.organisationRoots);
            searchParams.studyRightOrganisationIds = getValueIfValid(this.studyRightOrganisations);
            searchParams.studyRightParentOrganisationIds = getValueIfValid(this.studyRightParentOrganisations);
            searchParams.responsiblePersonId = getValueIfValid(this.responsibilityInfos) || getValueIfValid(this.responsiblePersonId);
            searchParams.documentState = getValueIfValid(this.documentStates);
            searchParams.universityOrgId = getValueIfValid(this.universityOrgId) || getValueIfValid(this.cooperationNetworkUniversityOrgId);
            searchParams.approvalState = getValueIfValid(this.approvalStates);
            searchParams.dateRange = getValueIfValid(this.dateRange);
            searchParams.dateOfBirth = getValueIfValid(this.dateOfBirth);
            searchParams.salesPeriod = getValueIfValid(this.salesPeriod);
            searchParams.validityPeriod = getValueIfValid(this.validityPeriod);
            searchParams.salesPeriodValidity = getValueIfValid(this.salesPeriodValidity);
            searchParams.productStates = getValueIfValid(this.productStates);
            searchParams.creditRange = getValueIfValid(this.creditRange);
            searchParams.messageType = getValueIfValid(this.messageType);
            searchParams.messageReadState = getValueIfValid(this.messageReadState);
            searchParams.studyRightTuitionFeeObligationPeriodRanges = getValueIfValid(this.studyRightTuitionFeeObligationPeriodRanges);

            // Already separated urns
            searchParams.attainmentLanguageUrn = getValueIfValid(this.attainmentLanguageUrns);
            searchParams.attainmentDate = getValueIfValid(this.attainmentDate);
            searchParams.assessmentItemType = getValueIfValid(this.assessmentItemType);
            searchParams.assessmentItemId = getValueIfValid(this.assessmentItemId);
            searchParams.studyFieldUrn = getValueIfValid(this.studyFieldUrns);
            searchParams.studyLevelUrn = getValueIfValid(this.studyLevelUrns);
            searchParams.motherTongue = getValueIfValid(this.motherTongue);
            searchParams.preferredLanguage = getValueIfValid(this.preferredLanguage);
            searchParams.schoolEducationLanguage = getValueIfValid(this.schoolEducationLanguage);
            searchParams.citizenship = getValueIfValid(this.citizenship);
            searchParams.gender = getValueIfValid(this.gender);
            searchParams.municipality = getValueIfValid(this.municipality);
            searchParams.educationType = getValueIfValid(this.educationType);
            searchParams.degreeProgramType = getValueIfValid(this.degreeProgramType);
            searchParams.studyRightExpirationRulesUrn = getValueIfValid(this.studyRightExpirationRulesUrn);
            searchParams.studyRightState = getValueIfValid(this.studyRightState);
            searchParams.studyRightSelectedOptions = getValueIfValid(this.studyRightSelectedOptions);
            searchParams.studyRightSelectedChildOptions = getValueIfValid(this.studyRightSelectedChildOptions);
            searchParams.studyRightMinorSelections = getValueIfValid(this.studyRightMinorSelections);
            searchParams.studyRightModuleSelections = getValueIfValid(this.studyRightModuleSelections);
            searchParams.courseUnits = getValueIfValid(this.courseUnits);
            searchParams.studyModules = getValueIfValid(this.studyModules);
            searchParams.attendingTermRegistrations = getValueIfValid(this.attendingTermRegistrations);
            searchParams.nonAttendingTermRegistrations = getValueIfValid(this.nonAttendingTermRegistrations);
            searchParams.educationTypeUrns = getValueIfValid(this.educationTypeUrns);
            searchParams.dead = getValueIfValid(this.dead);
            searchParams.personGroupType = getValueIfValid(this.personGroupType);
            searchParams.personGroupUpdateType = getValueIfValid(this.personGroupUpdateType);

            searchParams.applicationState = getValueIfValid(this.applicationState);
            searchParams.applicationType = getValueIfValid(this.applicationType);
            searchParams.workflowState = getValueIfValid(this.workflowState);
            searchParams.workflowType = getValueIfValid(this.workflowType);
            searchParams.responsibleTutorId = getValueIfValid(this.responsibleTutorId);
            searchParams.responsibleInHandlingId = getValueIfValid(this.responsibleInHandlingId);
            searchParams.responsibleType = getValueIfValid(this.responsibleType);

            searchParams.attainmentState = getValueIfValid(this.attainmentState);
            searchParams.attainmentType = getValueIfValid(this.attainmentType);

            searchParams.qualificationType = getValueIfValid(this.qualificationType);
            searchParams.qualificationState = getValueIfValid(this.qualificationState);
            searchParams.competencyState = getValueIfValid(this.competencyState);

            searchParams.moduleType = getValueIfValid(this.moduleType);
            searchParams.messageType = getValueIfValid(this.messageType);
            searchParams.enrolmentState = getValueIfValid(this.enrolmentState);
            searchParams.studySubGroupId = getValueIfValid(this.studySubGroupId);

            searchParams.studyRightSelectionType = getValueIfValid(this.studyRightSelectionType);

            searchParams.misregistration = getValueIfValid(this.attainmentMisregistration);
            searchParams.attached = getValueIfValid(this.attainmentAttached);
            searchParams.primary = getValueIfValid(this.attainmentPrimary);

            searchParams.cooperationNetworkId = getValueIfValid(this.cooperationNetworks);
            searchParams.cooperationNetworkIdsForUniversityOrgId = getValueIfValid(this.cooperationNetworkUniversities);
            searchParams.validityInCooperationNetwork = getValueIfValid(this.validityInCooperationNetwork);
            searchParams.excludeUniversityOrgId = getValueIfValid(this.excludeUniversityOrgId);

            // Combine all urns to params.codeUrn
            searchParams.codeUrn = _.union(
                getValueIfValid(this.officialLanguageUrns),
                getValueIfValid(this.attainmentLanguageUrns),
                getValueIfValid(this.studyLevelUrns),
                getValueIfValid(this.studyFieldUrns),
                getValueIfValid(this.courseUnitTypeUrns),
                getValueIfValid(this.cefrLevelUrns),
                getValueIfValid(this.subjectUrns),
                getValueIfValid(this.degreeProgrammeTypeUrns),
                getValueIfValid(this.educationClassificationModelUrns),
                getValueIfValid(this.municipalityUrns),
                getValueIfValid(this.courseUnitRealisationTypeUrn),
                getValueIfValid(this.studyRightSelectionType),
            );

            // Combine all course unit realisation urns to params.curCodeUrn
            searchParams.curCodeUrn = _.union(getValueIfValid(this.curTeachingLanguageUrns));

            // activityPeriod and validityPeriod
            // Both of these are found on search APIs and should be refactored to use only one
            searchParams.activityPeriod = _.union(
                getValueIfValid(this.studyYears),
                getValueIfValid(this.studyTerms),
                getValueIfValid(this.studyPeriods),
            );

            searchParams.validityPeriod = _.union(
                getValueIfValid(this.studyYears),
                getValueIfValid(this.studyTerms),
                getValueIfValid(this.studyPeriods),
            );

            if (this.flowStates.isValidForSearch()) {
                _.forEach(this.flowStates.getValues(), flowState => {
                    switch (flowState.id) {
                        case SEARCH_FLOWS_STATE.PUBLISHED_ACTIVE:
                            appendFlowState('PUBLISHED');
                            searchParams.publishFuture = false;
                            break;
                        case SEARCH_FLOWS_STATE.PUBLISHED_FUTURE:
                            appendFlowState('PUBLISHED');
                            searchParams.publishFuture = true;
                            break;
                        default:
                            appendFlowState(flowState.id);
                    }
                });
                if (_.size(this.flowStates.getValues()) > 1) {
                    searchParams.publishFuture = undefined;
                }
            }
            if (this.educations.isValidForSearch()) {
                searchParams.educationIds = _.map(this.educations.getValues(), 'id');
                const learningOpportunities = _.flatMap(this.educations.getValues(), value =>
                    _.map(value.children, childValue => `${value.id},${childValue.localId}`),
                );
                if (_.isEmpty(learningOpportunities)) {
                    searchParams.learningOpportunities = undefined;
                } else {
                    searchParams.learningOpportunities = learningOpportunities;
                }
            } else {
                searchParams.educationIds = undefined;
            }

            if (this.activePhases.isValidForSearch()) {
                searchParams.activePhaseModuleGroupIds = _.map(this.activePhases.getValues(), 'groupId');
            } else {
                searchParams.activePhaseModuleGroupIds = undefined;
            }

            searchParams.sort = getValueIfValid(this.sort);

            searchParams.showMaxResults = getValueIfValid(this.showMaxResults);
            if (searchParams.showMaxResults) {
                searchParams.limit = searchDefaults.maxLimit;
            }

            if (this.validity) {
                searchParams.validity = this.validity;
            }

            _.assign(requestOptions.params, this.overridingDefaults);

            return requestOptions;
        };

        return SearchParameters;
    }
})();
